import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Building, Globe, Phone, MapPin, Mail } from 'lucide-react';
import { motion } from 'framer-motion';
import api from '../services/api';

const CompanySetup = () => {
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    country: '',
    zip: ''
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api.post('/company', formData);
      toast.success('Company profile created successfully!');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save company profile');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-6">
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-slate-100 p-8"
        >
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-slate-800 mb-2">Company Profile</h1>
            <p className="text-slate-600">Tell us about your business to get started</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Company Name *
              </label>
              <div className="relative">
                <Building className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                <input
                  name="name"
                  data-testid="company-name-input"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full pl-11 pr-3 py-3 border border-slate-200 rounded-md focus:ring-2 focus:ring-emerald-900 focus:border-emerald-900"
                  placeholder="Your Company Ltd."
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Company Website
              </label>
              <div className="relative">
                <Globe className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                <input
                  name="url"
                  data-testid="company-url-input"
                  type="url"
                  value={formData.url}
                  onChange={handleChange}
                  className="w-full pl-11 pr-3 py-3 border border-slate-200 rounded-md focus:ring-2 focus:ring-emerald-900 focus:border-emerald-900"
                  placeholder="https://yourcompany.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Phone Number
              </label>
              <div className="relative">
                <Phone className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                <input
                  name="phone"
                  data-testid="company-phone-input"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full pl-11 pr-3 py-3 border border-slate-200 rounded-md focus:ring-2 focus:ring-emerald-900 focus:border-emerald-900"
                  placeholder="+1 (555) 000-0000"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Street Address
              </label>
              <div className="relative">
                <MapPin className="absolute left-3 top-3 w-5 h-5 text-slate-400" />
                <input
                  name="address"
                  data-testid="company-address-input"
                  value={formData.address}
                  onChange={handleChange}
                  className="w-full pl-11 pr-3 py-3 border border-slate-200 rounded-md focus:ring-2 focus:ring-emerald-900 focus:border-emerald-900"
                  placeholder="123 Business Street"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  City
                </label>
                <input
                  name="city"
                  data-testid="company-city-input"
                  value={formData.city}
                  onChange={handleChange}
                  className="w-full px-3 py-3 border border-slate-200 rounded-md focus:ring-2 focus:ring-emerald-900 focus:border-emerald-900"
                  placeholder="New York"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  State/Province
                </label>
                <input
                  name="state"
                  data-testid="company-state-input"
                  value={formData.state}
                  onChange={handleChange}
                  className="w-full px-3 py-3 border border-slate-200 rounded-md focus:ring-2 focus:ring-emerald-900 focus:border-emerald-900"
                  placeholder="NY"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Country
                </label>
                <input
                  name="country"
                  data-testid="company-country-input"
                  value={formData.country}
                  onChange={handleChange}
                  className="w-full px-3 py-3 border border-slate-200 rounded-md focus:ring-2 focus:ring-emerald-900 focus:border-emerald-900"
                  placeholder="United States"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  ZIP/Postal Code
                </label>
                <input
                  name="zip"
                  data-testid="company-zip-input"
                  value={formData.zip}
                  onChange={handleChange}
                  className="w-full px-3 py-3 border border-slate-200 rounded-md focus:ring-2 focus:ring-emerald-900 focus:border-emerald-900"
                  placeholder="10001"
                />
              </div>
            </div>

            <button
              type="submit"
              data-testid="company-submit-button"
              disabled={loading}
              className="w-full bg-emerald-900 text-white py-3 px-8 rounded-md font-medium hover:bg-emerald-800 transition-all shadow-lg hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Continue to Dashboard'}
            </button>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default CompanySetup;
